<template>
	<div class="flowchart-container" id="experience_flowchart"
		@mousemove="handleMove" 
		@mouseup="handleUp"
		@mousedown="handleDown">
		<svg width="100%" :height="`${height}px`">
			<experience-link v-bind.sync="link" 
				v-for="(link, index) in lines" 
				:key="`link${index}`"
				@editLink="editLink(link.id)">
			</experience-link>
		</svg>
		<experience-node v-bind.sync="node" 
			v-for="(node, index) in scene.nodes" 
			:key="`node${index}`"
			:options="nodeOptions"
			@linkingStart="linkingStart(node.id)"
			@linkingStop="linkingStop(node.id)"
			@editDetails="editDetails(node)"
			@editStep="editStep(node)"
			@deleteStep="confirmNodeDelete(node.id)"
			@nodeSelected="nodeSelected(node.id, $event)">
		</experience-node>

		<modal v-if="show.deleteLink" :withOkBtn="true" :withCancelBtn="true" @close="show.deleteLink = false" @ok="deleteLink">
			<div slot="header">Delete Link</div>
			<div slot="body">
				Are you sure you want to delete this link?
			</div>
		</modal>

		<modal v-if="show.deleteStep" :withOkBtn="true" :withCancelBtn="true" @close="show.deleteStep = false" @ok="deleteStep">
			<div slot="header">Delete Node</div>
			<div slot="body">
				Are you sure you want to delete this node?
			</div>
		</modal>

		<modal v-if="show.editedStep" :withOkBtn="true" :withCancelBtn="true" @close="show.editedStep = false" @ok="updateNode">
			<div slot="header">Edit Node</div>
			<div slot="body">
				<div class="form-group">
					<input class="form-input" type="text" v-model="editedStep.title" placeholder="Edit Title">
				</div>
				<div class="form-group">
					<textarea class="form-input" type="text" v-model="editedStep.description" placeholder="Add a small description" rows="5"></textarea>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
	import ExperienceLink from './ExperienceLink.vue';
	import ExperienceNode from './ExperienceNode.vue';
	import { getMousePosition } from '../utils/position';
	import Modal from './modals/Modal'
	import {mapGetters} from 'vuex'

	export default {
		name: 'ExperienceFlowchart',
		props: {
			height: {
				type: Number,
				default: 400,
			},
		},
		components: {
			ExperienceLink,
			ExperienceNode,
			'modal': Modal
		},
		computed: {
			...mapGetters(['scene']),
			nodeOptions() {
				return {
					centerY: this.centerY,
					centerX: this.centerX,
					scale: this.scale,
					offsetTop: this.rootDivOffset.top,
					offsetLeft: this.rootDivOffset.left,
					selected: this.action.selected,
				}
			},
			lines() {
				const lines = this.scene.links.map((link) => {
					const fromNode = this.findNodeWithID(link.from)
					const toNode = this.findNodeWithID(link.to)
					let x, y, cy, cx, ex, ey;
					x = this.centerX + fromNode.x;
					y = this.centerY + fromNode.y;
					[cx, cy] = this.getPortPosition('bottom', x, y);
					x = this.centerX + toNode.x;
					y = this.centerY + toNode.y;
					[ex, ey] = this.getPortPosition('top', x, y);
					return { 
						start: [cx, cy],
						end: [ex, ey],
						id: link.id,
					};
				})
				if (this.draggingLink) {
					let x, y, cy, cx;
					const fromNode = this.findNodeWithID(this.draggingLink.from)
					x = this.centerX + fromNode.x;
					y = this.centerY + fromNode.y;
					[cx, cy] = this.getPortPosition('bottom', x, y);
					// push temp dragging link, mouse cursor postion = link end postion 
					lines.push({ 
						start: [cx, cy], 
						end: [this.draggingLink.mx, this.draggingLink.my],
					})
				}
				return lines;
			}
		},
		data() {
			return {
				centerX: 1024,
				scale: 1,
				centerY: 140,
				show: {
					deleteLink: false,
					editedStep: false,
					deleteStep: false
				},
				action: {
					linking: false,
					dragging: false,
					scrolling: false,
					selected: 0,
				},
				mouse: {
					x: 0,
					y: 0,
					lastX: 0,
					lastY: 0,
				},
				draggingLink: null,
				rootDivOffset: {
					top: 0,
					left: 0
				},
				selectedLinkId: null,
				selectedNodeId: null,
				editedStep: {
					id: null,
					title: '',
					description: ''
				}
			};
		},
		mounted() {
			this.rootDivOffset.top = this.$el ? this.$el.offsetTop : 0;
			this.rootDivOffset.left = this.$el ? this.$el.offsetLeft : 0;
			// console.log(22222, this.rootDivOffset);
		},
		methods: {
			findNodeWithID(id) {
				return this.scene.nodes.find((item) => {
						return id === item.id
				})
			},
			getPortPosition(type, x, y) {
				if (type === 'top') {
					return [x + 60, y];
				}
				else if (type === 'bottom') {
					return [x + 60, y + 120];
				}
			},
			linkingStart(index) {
				this.action.linking = true;
				this.draggingLink = {
					from: index,
					mx: 0,
					my: 0,
				};
			},
			linkingStop(index) {
				// add new Link
				if (this.draggingLink && this.draggingLink.from !== index) {
					// check link existence
					const existed = this.scene.links.find((link) => {
						return link.from === this.draggingLink.from && link.to === index;
					})
					if (!existed) {
						let maxID = Math.max(0, ...this.scene.links.map((link) => {
							return link.id
						}))
						const newLink = {
							id: maxID + 1,
							from: this.draggingLink.from,
							to: index,
						};
						this.$store.dispatch({
							'type': 'addSceneLink',
							'newLink': newLink
						});
					}
				}
				this.draggingLink = null
			},
			nodeSelected(id, e) {
				this.action.dragging = id;
				this.action.selected = id;
				this.$emit('nodeClick', id);
				this.mouse.lastX = e.pageX || e.clientX + document.documentElement.scrollLeft;
				this.mouse.lastY = e.pageY || e.clientY + document.documentElement.scrollTop;
				setTimeout(function(){
					let selectedNode = document.getElementsByClassName("story-node selected")[0];
					document.getElementById("experience_flowchart").appendChild(selectedNode);
				}, 300);
			},
			handleMove(e) {
				if (this.action.linking) {
					[this.mouse.x, this.mouse.y] = getMousePosition(this.$el, e);
					[this.draggingLink.mx, this.draggingLink.my] = [this.mouse.x, this.mouse.y];
				}
				if (this.action.dragging) {
					this.mouse.x = e.pageX || e.clientX + document.documentElement.scrollLeft;
					this.mouse.y = e.pageY || e.clientY + document.documentElement.scrollTop;
					let diffX = this.mouse.x - this.mouse.lastX;
					let diffY = this.mouse.y - this.mouse.lastY;
					this.mouse.lastX = this.mouse.x;
					this.mouse.lastY = this.mouse.y;
					this.moveSelectedNode(diffX, diffY);
				}
				if (this.action.scrolling) {
					[this.mouse.x, this.mouse.y] = getMousePosition(this.$el, e);
					let diffX = this.mouse.x - this.mouse.lastX;
					let diffY = this.mouse.y - this.mouse.lastY;
					this.mouse.lastX = this.mouse.x;
					this.mouse.lastY = this.mouse.y;
					this.centerX += diffX;
					this.centerY += diffY;
					// this.hasDragged = true
				}
			},
			handleUp(e) {
				const target = e.target || e.srcElement;
				if (this.$el.contains(target)) {
					if (typeof target.className !== 'string' || target.className.indexOf('node-input') < 0) {
						this.draggingLink = null;
					}
				}
				this.action.linking = false;
				this.action.dragging = null;
				this.action.scrolling = false;
			},
			handleDown(e) {
				const target = e.target || e.srcElement;
				// console.log('for scroll', target, e.keyCode, e.which)
				if ((target === this.$el || target.matches('svg, svg *')) && e.which === 1) {
					this.action.scrolling = true;
					[this.mouse.lastX, this.mouse.lastY] = getMousePosition(this.$el, e);
					this.action.selected = null; // deselectAll
				}
				this.$emit('canvasClick', e);
			},
			moveSelectedNode(dx, dy) {
				let index = this.scene.nodes.findIndex((item) => {
					return item.id === this.action.dragging
				})
				let left = this.scene.nodes[index].x + dx / this.scale;
				let top = this.scene.nodes[index].y + dy / this.scale;

				this.$store.dispatch({
					'type': 'updateSceneNodePosition',
					'nodeIndex': index,
					'left': left,
					'top': top
				});
			},
			editLink(linkId) {
				console.log(linkId);
				this.show.deleteLink = true;
				this.selectedLinkId = linkId;
			},
			deleteLink() {
				let linkId = this.selectedLinkId;
				const deletedLink = this.scene.links.find((item) => {
						return item.id === linkId;
				});
				if (deletedLink) {
					this.$store.dispatch({
						'type': 'deleteSceneLink',
						'linkId': linkId
					}).then(response => {
						this.selectedLinkId = null;
					});
				}
				this.show.deleteLink = false;
			},
			confirmNodeDelete(id) {
				this.show.deleteStep = true;
				this.selectedNodeId = id;
			},
			deleteStep() {
				let nodeId = this.selectedNodeId;
				this.$store.dispatch({
					'type': 'deleteSceneNode',
					'nodeId': nodeId
				}).then(response => {
					this.selectedNodeId = null;
				});
				this.show.deleteStep = false;
			},
			editStep(node) {
				console.log('edit node: ', node.id);
				this.$emit('editStep', node);
			},
			editDetails(node) {
				console.log('edit title: ', node);
				this.editedStep.id = node.id;
				this.editedStep.title = node.title;
				this.editedStep.description = node.description;
				this.show.editedStep = true;
			},
			updateNode() {
				this.$emit('editDetails', this.editedStep);
				this.show.editedStep = false;
			}
		}
	}
</script>

<style scoped lang="scss">
	.flowchart-container {
		margin: 0;
		background: rgba(0,0,0,0.2);
		position: relative;
		overflow: hidden;
		svg {
			cursor: grab;
		}
	}
</style>